/* eslint-disable @typescript-eslint/no-explicit-any */

import _ from "lodash";

import type {
    CollectionPropertyType,
    ObjectArrayPropertyType,
    ObjectPropertyType,
    SimpleArrayPropertyType,
    SimplePropertyType,
    ValidationErrorType,
    ValidatorInterface,
    ValidatorInterfaceParams,
} from "../types/validation";

const addError = (errors: Array<ValidationErrorType>, newErrors: Array<ValidationErrorType>) => {
    newErrors.forEach((error) => {
        errors.push(error);
    });
};

const validateArrayProperty = (
    property: SimpleArrayPropertyType | ObjectArrayPropertyType,
    array: Array<any>,
    errors: Array<ValidationErrorType>,
    path: string
): boolean => {
    if (Array.isArray(array)) {
        if (property.min !== undefined && array.length < property.min) {
            errors.push({
                path: path,
                code: "notEnoughElements",
            });
        }

        if (property.max !== undefined && array.length > property.max) {
            errors.push({
                path: path,
                code: "tooManyElements",
            });
        }

        return true;
    } else {
        errors.push({
            path: path,
            code: "invalidArray",
        });

        return false;
    }
};

const validateSimpleProperty = ({
    property,
    name,
    value,
    object,
    errors,
    path,
}: {
    property: SimplePropertyType;
    name?: string;
    value: any;
    object?: any;
    errors: Array<ValidationErrorType>;
    path: string;
}) => {
    property.validators.forEach((validator) => {
        addError(
            errors,
            validator({
                property,
                name,
                value,
                object,
                path,
            })
        );
    });
};

const validateCollectionProperty = (
    property: CollectionPropertyType,
    collection: any,
    errors: Array<ValidationErrorType>,
    path: string
) => {
    const collectionSize = _.size(collection);

    if (property.min !== undefined && collectionSize < property.min) {
        errors.push({
            path: path,
            code: "notEnoughElements",
        });
    }

    if (property.max !== undefined && collectionSize > property.max) {
        errors.push({
            path: path,
            code: "tooManyElements",
        });
    }

    _.forEach(collection, (item, key) => {
        validateObjectProperty(property.of, item, errors, path + "." + key);
    });
};

const validateSimpleArrayProperty = (
    property: SimpleArrayPropertyType,
    array: Array<any>,
    errors: Array<ValidationErrorType>,
    path: string
) => {
    if (validateArrayProperty(property, array, errors, path)) {
        array.forEach((value: any, index: number) => {
            validateSimpleProperty({
                property: property.of,
                value,
                errors,
                path: path + "." + index,
            });
        });
    }
};

const validateObjectProperty = (
    objectProperty: ObjectPropertyType,
    object: any,
    errors: Array<ValidationErrorType>,
    path: string
) => {
    if (Array.isArray(objectProperty.validators)) {
        objectProperty.validators.forEach((validator) => {
            addError(
                errors,
                validator({
                    property: objectProperty,
                    object,
                    path,
                })
            );
        });
    }

    _.forEach(objectProperty.properties, (property, name) => {
        const newPath = (path.length > 0 ? path + "." : "") + name;
        const value = object ? object[name] : undefined;

        if (property.type === "simple") {
            validateSimpleProperty({
                property,
                name,
                value,
                object,
                errors,
                path: newPath,
            });
        }

        if (property.type === "simpleArray") {
            validateSimpleArrayProperty(property, value, errors, newPath);
        }

        if (property.type === "object") {
            validateObjectProperty(property, value, errors, newPath);
        }

        if (property.type === "objectArray") {
            validateObjectArrayProperty(property, value, errors, newPath);
        }

        if (property.type === "collection") {
            validateCollectionProperty(property, value, errors, newPath);
        }
    });
};

const validateObjectArrayProperty = (
    property: ObjectArrayPropertyType,
    array: Array<any>,
    errors: Array<ValidationErrorType>,
    path: string
) => {
    if (Array.isArray(property.validators)) {
        property.validators.forEach((validator) => {
            addError(
                errors,
                validator({
                    property,
                    array,
                    path,
                })
            );
        });
    }

    if (validateArrayProperty(property, array, errors, path)) {
        array.forEach((value: any, index: number) => {
            validateObjectProperty(property.of, value, errors, path + "." + index);
        });
    }
};

const validate = (property: ObjectPropertyType, object: any): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    validateObjectProperty(property, object, errors, "");

    return errors;
};

export default validate;

export const valueConfirmValidatorFactory = (propertyNameToConfirm: string): ValidatorInterface => {
    return ({ name, object, path }: ValidatorInterfaceParams): Array<ValidationErrorType> => {
        const errors: Array<ValidationErrorType> = [];

        if (name && object) {
            if (
                !(name in object) ||
                !(propertyNameToConfirm in object) ||
                object[name] !== object[propertyNameToConfirm]
            ) {
                errors.push({
                    path: path,
                    code: "unableToConfirm",
                    detail: name,
                });
            }
        }

        return errors;
    };
};

export const notEmptyValidator = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    if (value === null || value === undefined || ("" + value).trim() === "") {
        errors.push({
            path: path,
            code: "notEmpty",
        });
    }

    return errors;
};

export const notEmptyHtmlValidator = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    if (
        value === null ||
        value === undefined ||
        ("" + value).trim() === "" ||
        ("" + value).replace(/<[^>]*>/g, "") === ""
    ) {
        errors.push({
            path: path,
            code: "notEmpty",
        });
    }

    return errors;
};

export const emailValidator = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

    if (!regex.test(value)) {
        errors.push({
            path: path,
            code: "email",
        });
    }

    return errors;
};

export const notEmptyEmailValidator = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    if (value !== "") {
        const regex =
            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

        if (!regex.test(value)) {
            errors.push({
                path: path,
                code: "email",
            });
        }
    }

    return errors;
};

export const emptyEmailValidator = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    const regex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

    if (value !== "" && !regex.test(value)) {
        errors.push({
            path: path,
            code: "email",
        });
    }

    return errors;
};

export const telephoneValidator = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    if (value) {
        const regex = /^([+]?\d{1,2}[-\s]?|)\d{3}[-\s]?\d{3}[-\s]?\d{3,4}$/;

        if (!regex.test(value)) {
            errors.push({
                path: path,
                code: "telephone",
            });
        }
    }

    return errors;
};

// Senza gli spazi
export const ibanValidatorIT = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    const regex = /[A-Z]{2}\d{2}[A-Z]\d{5}\d{5}[A-Z0-9]{12}/g;

    if (!regex.test(value)) {
        errors.push({
            path: path,
            code: "ibanIT",
        });
    }

    return errors;
};

// Con gli spazi
export const ibanUiValidatorIT = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    const regex = /[A-Z]{2}\s\d{2}\s[A-Z]\s\d{5}\s\d{5}\s[A-Z0-9]{12}/g;

    if (!regex.test(value)) {
        errors.push({
            path: path,
            code: "ibanUiIT",
        });
    }

    return errors;
};

export const ibanValidatorITOptionalBlankSpace = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    const regexWithBlankSpaces = /[A-Z]{2}\s\d{2}\s[A-Z]\s\d{5}\s\d{5}\s[A-Z0-9]{12}/g;
    const regexWithoutBlankSpaces = /[A-Z]{2}\d{2}[A-Z]\d{5}\d{5}[A-Z0-9]{12}/g;

    if (!regexWithBlankSpaces.test(value) && !regexWithoutBlankSpaces.test(value)) {
        errors.push({
            path: path,
            code: "ibanValidatorITOptionalBlankSpace",
        });
    }

    return errors;
};

const getIbanRegexes = (onlyEurope: boolean): { [x: string]: RegExp } => {
    const ibanRegexes: { [x: string]: { regex: RegExp; europe: boolean } } = {
        AL: {
            // Albania
            regex: /^AL\d{10}[0-9A-Z]{16}$/,
            europe: true,
        },
        AD: {
            // Andorra
            regex: /^AD\d{10}[0-9A-Z]{12}$/,
            europe: true,
        },
        AT: {
            // Austria
            regex: /^AT\d{18}$/,
            europe: true,
        },
        BH: {
            // Kingdom of Bahrain
            regex: /^BH\d{2}[A-Z]{4}[0-9A-Z]{14}$/,
            europe: false,
        },
        BE: {
            // Belgium
            regex: /^BE\d{14}$/,
            europe: true,
        },
        BA: {
            // Bosnia and Herzegovina
            regex: /^BA\d{18}$/,
            europe: true,
        },
        BG: {
            // Bulgaria
            regex: /^BG\d{2}[A-Z]{4}\d{6}[0-9A-Z]{8}$/,
            europe: true,
        },
        HR: {
            // Croatia
            regex: /^HR\d{19}$/,
            europe: true,
        },
        CY: {
            // Cyprus
            regex: /^CY\d{10}[0-9A-Z]{16}$/,
            europe: true,
        },
        CZ: {
            //Czech Republic
            regex: /^CZ\d{22}$/,
            europe: true,
        },
        DK: {
            // Denmark
            regex: /^DK\d{16}$/,
            europe: true,
        },
        FO: {
            // Denmark
            regex: /^FO\d{16}$/,
            europe: true,
        },
        GL: {
            // Denmark
            regex: /^GL\d{16}$/,
            europe: true,
        },
        DO: {
            // Dominican Republic
            regex: /^DO\d{2}[0-9A-Z]{4}\d{20}$/,
            europe: false,
        },
        EE: {
            // Estonia
            regex: /^EE\d{18}$/,
            europe: true,
        },
        FI: {
            // Finland
            regex: /^FI\d{16}$/,
            europe: true,
        },
        FR: {
            // France
            regex: /^FR\d{12}[0-9A-Z]{11}\d{2}$/,
            europe: true,
        },
        GE: {
            // Georgia
            regex: /^GE\d{2}[A-Z]{2}\d{16}$/,
            europe: true,
        },
        DE: {
            // Germany
            regex: /^DE\d{20}$/,
            europe: true,
        },
        GI: {
            // Gibraltar
            regex: /^GI\d{2}[A-Z]{4}[0-9A-Z]{15}$/,
            europe: false,
        },
        GR: {
            // Greece
            regex: /^GR\d{9}[0-9A-Z]{16}$/,
            europe: true,
        },
        HU: {
            // Hungary
            regex: /^HU\d{26}$/,
            europe: true,
        },
        IS: {
            // Iceland
            regex: /^IS\d{24}$/,
            europe: true,
        },
        IE: {
            // Ireland
            regex: /^IE\d{2}[A-Z]{4}\d{14}$/,
            europe: true,
        },
        IL: {
            // Israel
            regex: /^IL\d{21}$/,
            europe: false,
        },
        IT: {
            // Italy
            regex: /^IT\d{2}[A-Z]\d{10}[0-9A-Z]{12}$/,
            europe: true,
        },
        KZ: {
            // Kazakhstan
            regex: /^[A-Z]{2}\d{5}[0-9A-Z]{13}$/,
            europe: false,
        },
        KW: {
            // KUWAIT
            regex: /^KW\d{2}[A-Z]{4}22!$/,
            europe: false,
        },
        LV: {
            // Latvia
            regex: /^LV\d{2}[A-Z]{4}[0-9A-Z]{13}$/,
            europe: true,
        },
        LB: {
            // LEBANON
            regex: /^LB\d{6}[0-9A-Z]{20}$/,
            europe: false,
        },
        LI: {
            // Liechtenstein (Principality of)
            regex: /^LI\d{7}[0-9A-Z]{12}$/,
            europe: true,
        },
        LT: {
            // Lithuania
            regex: /^LT\d{18}$/,
            europe: true,
        },
        LU: {
            // Luxembourg
            regex: /^LU\d{5}[0-9A-Z]{13}$/,
            europe: true,
        },
        MK: {
            // Macedonia
            regex: /^MK\d{5}[0-9A-Z]{10}\d{2}$/,
            europe: true,
        },
        MT: {
            // Malta
            regex: /^MT\d{2}[A-Z]{4}\d{5}[0-9A-Z]{18}$/,
            europe: true,
        },
        MR: {
            // Mauritania
            regex: /^MR13\d{23}$/,
            europe: false,
        },
        MU: {
            // Mauritius
            regex: /^MU\d{2}[A-Z]{4}\d{19}[A-Z]{3}$/,
            europe: false,
        },
        MC: {
            // Monaco
            regex: /^MC\d{12}[0-9A-Z]{11}\d{2}$/,
            europe: true,
        },
        ME: {
            // Montenegro
            regex: /^ME\d{20}$/,
            europe: true,
        },
        NL: {
            // The Netherlands
            regex: /^NL\d{2}[A-Z]{4}\d{10}$/,
            europe: true,
        },
        NO: {
            // Norway
            regex: /^NO\d{13}$/,
            europe: true,
        },
        PL: {
            // Poland
            regex: /^PL\d{10}[0-9A-Z]{},16}n$/,
            europe: true,
        },
        PT: {
            // Portugal
            regex: /^PT\d{23}$/,
            europe: true,
        },
        RO: {
            // Romania
            regex: /^RO\d{2}[A-Z]{4}[0-9A-Z]{16}$/,
            europe: true,
        },
        SM: {
            // San Marino
            regex: /^SM\d{2}[A-Z]\d{10}[0-9A-Z]{12}$/,
            europe: true,
        },
        SA: {
            // Saudi Arabia
            regex: /^SA\d{4}[0-9A-Z]{18}$/,
            europe: false,
        },
        RS: {
            // Serbia
            regex: /^RS\d{20}$/,
            europe: true,
        },
        SK: {
            // Slovak Republic
            regex: /^SK\d{22}$/,
            europe: true,
        },
        SI: {
            // Slovenia
            regex: /^SI\d{17}$/,
            europe: true,
        },
        ES: {
            // Spain
            regex: /^ES\d{22}$/,
            europe: true,
        },
        SE: {
            // Sweden
            regex: /^SE\d{22}$/,
            europe: true,
        },
        CH: {
            // Switzerland
            regex: /^CH\d{7}[0-9A-Z]{12}$/,
            europe: true,
        },
        TN: {
            // Tunisia
            regex: /^TN59\d{20}$/,
            europe: true,
        },
        TR: {
            // Turkey
            regex: /^TR\d{7}[0-9A-Z]{17}$/,
            europe: true,
        },
        AE: {
            // United Arab Emirates
            regex: /^AE\d{21}$/,
            europe: false,
        },
        GB: {
            // United Kingdom
            regex: /^GB\d{2}[A-Z]{4}\d{14}$/,
            europe: true,
        },
    };

    const tmpMap: { [x: string]: RegExp } = {};

    _.forEach(ibanRegexes, (ibanRegex, country) => {
        if (onlyEurope) {
            if (ibanRegex.europe === true) {
                tmpMap[country] = ibanRegex.regex;
            }
        } else {
            tmpMap[country] = ibanRegex.regex;
        }
    });

    return tmpMap;
};

export const ibanValidatorEU = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    if (value) {
        const ibanRegex = getIbanRegexes(true)[value.slice(0, 2)];

        if (ibanRegex === undefined || (ibanRegex && !ibanRegex.test(value))) {
            errors.push({
                path: path,
                code: "ibanEU",
            });
        }
    }

    return errors;
};

export const ibanValidatorWorld = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    if (value) {
        const ibanRegex = getIbanRegexes(false)[value.slice(0, 2)];

        if (ibanRegex === undefined || (ibanRegex && !ibanRegex.test(value))) {
            errors.push({
                path: path,
                code: "ibanWorld",
            });
        }
    }

    return errors;
};

export const uuidValidator = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    const regex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

    if (!regex.test(value)) {
        errors.push({
            path: path,
            code: "uuid",
        });
    }

    return errors;
};

export const fiscalCodePersonValidator = ({
    value,
    path,
}: ValidatorInterfaceParams): Array<ValidationErrorType> => {
    const errors: Array<ValidationErrorType> = [];

    const valueToCheck = ("" + value).toUpperCase();

    const nameSurnameRegex = /^([A-Z]){3}([A-Z]){3}$/g;
    const dateRegex =
        /^[\dLMNP-V]{2}([A-EHLMPR-T]([04LQ][1-9MNP-V]|[1256LMRS][\dLMNP-V])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM])$/g;
    const placeRegex = /^([A-ILMZ]{1}([0-9L-NP-V]){3})([A-Z]{1})$/g;

    const nameSurname = valueToCheck.substr(0, 6);
    const date = valueToCheck.substr(6, 5);
    const place = valueToCheck.substr(11);

    if (!nameSurnameRegex.test(nameSurname) || !dateRegex.test(date) || !placeRegex.test(place)) {
        errors.push({
            path: path,
            code: "fiscalCodePerson",
        });
    }

    return errors;
};

export const checkedValidator: ValidatorInterface = ({ value, path }) => {
    const errors: Array<ValidationErrorType> = [];

    if (value !== true) {
        errors.push({
            path,
            code: "requiredCheckbox",
        });
    }

    return errors;
};

export const getErrorDescription = (error: ValidationErrorType, lang?: string): string => {
    const mappingIt: { [x: string]: string } = {
        notEmpty: "Campo obbligatorio",
        email: "Email non valida",
        telephone: "Numero non valido",
        fiscalCodePerson: "Codice fiscale non valido",
        dateOfBirth: "Data non valida",
        ibanIT: "IBAN non valido",
        ibanUiIT: "IBAN non valido",
        ibanEU: "IBAN non valido",
        ibanWorld: "IBAN non valido",
        uuid: "UUID non valido",
        unableToConfirm: "Valore non confermato",
        requiredCheckbox: "Obbligatorio",
        ibanValidatorITOptionalBlankSpace: "IBAN non valido",
        positiveAndDifferentFromZeroValidator: "Numero non valido",
    };

    const mappingEn: { [x: string]: string } = {
        notEmpty: "Mandatory field",
        email: "Invalid email",
        telephone: "Invalid number",
        fiscalCodePerson: "Invalid fiscal code",
        dateOfBirth: "Invalid date",
        ibanIT: "Invalid IBAN",
        ibanUiIT: "Invalid IBAN",
        ibanEU: "Invalid IBAN",
        ibanWorld: "Invalid IBAN",
        uuid: "Invalid UUID",
        unableToConfirm: "Value not confirmed",
        requiredCheckbox: "Mandatory",
        ibanValidatorITOptionalBlankSpace: "Invalid IBAN",
        positiveAndDifferentFromZeroValidator: "Invalid number",
    };

    if (lang === "IT" || lang === undefined) {
        if (error.code in mappingIt) {
            return mappingIt[error.code];
        }
    }

    if (lang === "EN") {
        if (error.code in mappingEn) {
            return mappingEn[error.code];
        }
    }

    return error.code;
};
